import {Contact} from '@/Agent/types';

export interface SsnFormsRequest {
    ssn: string;
}

export interface AssociateSsnWithFormRequest {
    ssn: string;
    form: SsnForm;
}

export interface SsnForm {
    formId: string;
    email: string;
}

export type UserInfo = {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    dob: string;
    age: number;
    gender: 'male' | 'female';
    address: string;
    state: string;
    zip: string;
    city: string;
};

export interface CalcFocusEmailRequest {
    userEmail: string;
    link: string;
}

export type HubspotProsperDealStages = 'create' | 'submit' | 'refer' | 'approve' | 'update' | 'docusignComplete';
export const DecisionProcessingChoice = {
    ShareDecisionLater: 'Book an Appointment and Share Decision Later',
    ShareDecisionNow: 'Share Decision Now',
} as const;

export type DecisionProcessingChoice = (typeof DecisionProcessingChoice)[keyof typeof DecisionProcessingChoice];

export interface HubspotProsperDealRequest {
    properties: {
        agenium_case_id?: string;
        agent_portal_case_id?: string;
        agent_portal_form_link?: string;
        agent_portal_case_link?: string;
        dealname?: string;
        amount?: string;
        applied_carrier?: string;
        client_s_state_region?: string;
        coverage?: string; // applied coverage amount
        currently_employed?:
            | 'Employed'
            | 'Homemaker - Spouse Employed'
            | 'Unemployed'
            | 'Retired'
            | 'Student'
            | 'Disability'
            | 'Other';
        currently_self_employed?: 'true' | 'false';
        date_of_birth?: string;
        hubspot_owner_id?: string; // deal owner
        occupation?: string;
        policy_type?:
            | 'IUL'
            | '10-year Term'
            | '15-year Term'
            | '20-year Term'
            | '30-year Term'
            | 'GUL'
            | 'Whole life'
            | 'Annuities'
            | 'Accidental'
            | 'VUL'
            | 'Guaranteed Issue'; // applied product type
        quoted_monthly_premium?: string;
        rate_class?: string; // applied rate class
        new__xfer_owner?: string; // xfer owner
        auto_created_by_agenium?: string;
        policy_owner?: string;
        applicant_address_city?: string;
        applicant_address_state?: string;
        applicant_address_street?: string;
        applicant_address_zipcode?: string;
        submitted_e_application?: string;
        initial_review_complete?: string;
        approved_rating?: 'Approved as applied' | 'Approved not as applied' | 'Approved better than applied';
        approved_rate_class?: string;
        monthly_premium?: string;
        approved_coverage_amount?: string;
        cash_value_at_65?: string;
        approved_date?: string;
        policy_number?: string;
        flat_extra_amount?: string;
        flat_extra_duration?: string;
        table_rating?: string;
        applied_table_rating?: string;
        max_face_amount?: string;
        product_code?: string;
        tobacco_use?: string;
        underwriting_class?: string;
        decline_reason?: string;
        decline_reason_detailed?: string;
        decline_date?: string;
        close_lost_reason?: string;
        close_lost_date?: string;
        final_coverage_amount?: string;
        final_product_type?:
            | 'IUL'
            | '10-year Term'
            | '15-year Term'
            | '20-year Term'
            | '30-year Term'
            | 'GUL'
            | 'Whole life'
            | 'Annuities'
            | 'Accidental'
            | 'VUL'
            | 'Guaranteed Issue';
        final_carrier?: string;
        final_monthly_premium?: string;
        paid_target_annual_premium?: string;
        re_issued?: 'true' | 'false';
        carrier_policy_effective_date__from_commission_statements_?: string;
        client_specified_draft_date?: string;
        policy_was_issued_date?: string;
        conditional_coverage?: 'Yes' | 'No';
        decision_processing_choice?: DecisionProcessingChoice;
        prosper_banking_info_provided_upfront?: 'Yes' | 'No';
        carrier_refer_reason?: string;
    };
}

type Pagination = {
    total: number;
    limit: number;
    currentPage: number;
    nextPage?: number;
    previousPage?: number;
};

type SuccessApiResponse<Data, Meta = unknown> = {
    success: true;
    message: string;
    data: Data;
} & (Meta extends {} ? {meta: Meta} : {});

type FailureApiResponse<Meta = unknown> = {
    success: false;
    message: string;
} & (Meta extends {} ? {meta: Meta} : {});

type ApiResponse<Data, Meta = unknown> = SuccessApiResponse<Data, Meta> | FailureApiResponse<Meta>;
type PaginatedApiResponse<Data> = ApiResponse<Data[], Pagination>;

interface DealData {
    dealId: string;
    updatedAt: string;
}

interface CreateDealDuplicateAdditionalResponse {
    existingDealId: string;
}

export type CreateDealApiResponse = ApiResponse<DealData> & CreateDealDuplicateAdditionalResponse;
export type UpdateProsperDealResponse = ApiResponse<DealData>;
export type GetContactsApiResponse = PaginatedApiResponse<Contact>;
export type GetFormsForContactByEmailApiResponse = ApiResponse<any>;

export enum BankVerificationState {
    PASS = 'PASS',
    PREVENT_NEXT_WITH_OVERRIDE = 'PREVENT_NEXT_WITH_OVERRIDE',
    PREVENT_NEXT_NO_OVERRIDE = 'PREVENT_NEXT_NO_OVERRIDE',
}

export interface AddressVerificationRequest {
    street: string;
    city: string;
    state: string;
    zip: string;
    unit?: string;
}

export interface AddressVerificationResponse {
    valid: boolean;
    standardizedAddress: {
        street: string;
        city: string;
        state: string;
        zip: string;
        unit?: string;
    };
    components: Array<{field: string; confirmed: boolean; issue?: string}>;
    missingComponents: string[];
    verdict: {
        addressComplete: boolean;
        validationGranularity: string;
        hasInferredComponents: boolean;
        hasReplacedComponents: boolean;
    };
}

export interface BankVerificationRequest {
    routingNumber: string;
    accountNumber: string;
    accountType: 1 | 2; // 1 for checking, 2 for savings
    customerEmail: string; // Required - customer email for consent check
    customerFirstName?: string; // Customer's first name for FCRA notices
    customerLastName?: string; // Customer's last name for FCRA notices
    customerGuid?: string; // Customer's GUID for consistent identification
    formId?: string; // Agent Portal form ID for audit tracking
    policyNumber?: string; // Policy number if EMC
    ageniumCaseId?: string; // Agenium case ID if EMC
}

export interface BankVerificationOverrideRequest {
    verificationId: string;
    accountHash: string;
    reason?: string;
}

export interface BankVerificationResponse {
    verificationId: string;
    state: BankVerificationState;
    failureCode?: string;
    failureMessage?: string;
    accountHash?: string; // Hash of the account for override tracking
}

export type AssistContentTrigger = {
    type: 'function';
    prequalFlow?: 'single' | 'multi';
    functionName?: string;
};

export type AssistContent = {
    id: string;
    tab: string;
    title?: string;
    priority: number;
    triggerDescription: string;
    trigger: AssistContentTrigger;
    scriptContentId?: string;
    script?: AssistContentItem;
    faqContentId?: string;
    faq?: AssistContentItem;
    tipsContentId?: string;
    tips?: AssistContentItem;
    active: boolean;
};

export type AssistContentItem = {
    id: string;
    content: string;
};
